import { getSVG } from './components/icons-loader.js';
import { slickSliderGenerator } from './components/sliders.js';
import { accordion } from './components/accordion.js';
import { desygnerFunctions } from './desygner';
import { isUserLogged } from './components/user.js';
import { waitForElementToExist } from './observers';

const MaxSizeMB = parseFloat(CONFIG.upload_max_file_size);
const MaxPages = parseInt(CONFIG.pdf_max_allowed_pages);
const redirectURL = 'https://desygner.com/create/designs';
const s3Folder = 'public/uploads/pdf';
let _adapterInstance = null;
let _clientAdapter = null;

function loadPdfJs () {
  const script = document.createElement('script');
  script.src = 'https://static.webrand.com/pdfjs/pdf.min.js';
  script.async = true;

  document.body.appendChild(script);
}

const modalElements = (modalId = null) => {
  return {
    modalContainer: document.querySelector(`#${modalId} .pdf-viewer-modal`),
    modal: document.querySelector(`#${modalId} .preloader`),
    modalText: document.querySelector(`#${modalId} .preloader .message`),
    modalButton: document.querySelector(`#${modalId} .preloader [data-toggle="close-modal"]`)
  };
};

document.addEventListener('DOMContentLoaded', function () {
  slickSliderGenerator('workflow_4_steps_big', '.workflow-big-container');
  accordion('.faqs-cards');
  getSVG();
  loadPdfJs();
  dragDropPdf();
  initButtonAndUploadListeners();
});

function handleEditButtonClicked () {
  const registerUserButton = document.querySelector('[data-toggle="register-modal"]');

  isUserLogged && (window.location.href = redirectURL);
  !isUserLogged && registerUserButton.click();
}

const initButtonAndUploadListeners = async () => {
  const waitForElement = (selector) =>
    (new Promise(resolve => {
      const fragment = document.querySelector(selector);

      if (fragment) {
        return resolve(fragment);
      }

      const observer = new MutationObserver(() => {
        const fragment = document.querySelector(selector);

        if (fragment) {
          resolve(fragment);
          observer.disconnect();
        }
      });
    }));

  const selectorInput = '#pdf-editor-upload';
  const selectorButton = '[data-toggle="pdf-upload"]';
  const selectorMobileMenu = '.mobile-menu button';

  await Promise.all([waitForElement(selectorInput), waitForElement(selectorButton)]);
  const uploadInput = document.querySelector(selectorInput);
  uploadInput.addEventListener('change', function () {showAndUploadPdf(this)});

  const uploadButton = document.querySelector(selectorButton);

  uploadButton.addEventListener('pointerdown', () => (uploadInput.value = ''));
  uploadButton.addEventListener('pointerup', () => uploadInput.click());

  document.querySelectorAll(selectorMobileMenu).forEach(mobileButton => {
    mobileButton.addEventListener('pointerenter', () => this.classList.add('enter'));
    mobileButton.addEventListener('pointerleave', () => this.classList.remove('enter'));
  });

  const modalIds = ['pdf-error-mimetype', 'pdf-error-filesize', 'pdf-error-page-length'];
  for (const modalId of modalIds) {
    modalElements(modalId).modalButton.addEventListener('mouseup', () => genericPdfErrorModal(false, modalId));
  }

  localStorage.removeItem('pdf-viewer-showing');
};

const dragDropPdf = () => {
  const dropArea = document.querySelector('.drop-area');
  const dropAreaBg = document.querySelector('.dragNdrop-background');
  const dropAreaIcon = document.querySelector('.drop-area .dragNDrop-icon');

  const isPdfFile = file => {
    return file.type === 'application/pdf';
  };

  dropArea.addEventListener('dragenter', event => {
    event.preventDefault();
    dropAreaBg.classList.add('active');
    dropAreaIcon.classList.add('active');
  });

  dropArea.addEventListener('dragover', event => event.preventDefault());

  dropArea.addEventListener('drag', event => {
    event.preventDefault();
    dropAreaBg.classList.add('active');
    dropAreaIcon.classList.add('active');
  });

  dropAreaBg.addEventListener('drop', event => {
    event.preventDefault();
    const dataTransfer = event.dataTransfer;

    if (dataTransfer.types.includes('Files') && Array.from(dataTransfer.files).some(isPdfFile)) {
      showAndUploadPdf(dataTransfer);
    } else {
      genericPdfErrorModal(true, 'pdf-error-mimetype');
    }

    dropAreaBg.classList.remove('active');
    dropAreaIcon.classList.remove('active');
  });

  dropAreaBg.addEventListener('dragleave', event => {
    event.preventDefault();
    dropAreaBg.classList.remove('active');
    dropAreaIcon.classList.remove('active');
  });
};

function genericPdfErrorModal (show = false, id = null, additionalText = null) {
  if (id) {
    const modal = document.querySelector(`#${id}`);

    if (show) {
      modal.style.display = 'flex';
      document.documentElement.classList.add('no-scroll');
      let currentText = modalElements(id).modalText.textContent;

      if (additionalText && !currentText.includes(additionalText)) {
        modalElements(id).modalText.textContent += ` ${additionalText}`;
      }
    } else {
      modal.style.display = 'none';
      document.documentElement.classList.remove('no-scroll');
    }
  }
}

const showAndUploadPdf = async event => {
  const file = event.files[0];
  _toggleSkeleton(true);

  try {
    checkFileSize(file);
    await checkPageLength(file);
  } catch (e) {
    console.error(e);
    _toggleSkeleton(false);
    return;
  }

  try {
    await initPdfViewerAureliaApp(file);
    _toggleSkeleton(false, false);
    const signatureData = await createS3Signature(file);
    await uploadFileToS3(signatureData, file);
    await setDesygnerRedirectionProperties(signatureData, file);
  } catch (e) {
    _toggleSkeleton(false);
    console.error(e);
    _adapterInstance.trigger('static_pages:pdf-upload-failure');
  }
};

function checkFileSize (file) {
  const fileSizeMB = file.size / (1024 * 1024);

  if (fileSizeMB > MaxSizeMB) {
    genericPdfErrorModal(true, 'pdf-error-filesize', `${MaxSizeMB}MB`);
    const message = `File size is over ${MaxSizeMB}MB`;
    console.error(message);
    throw new Error(message);
  }
}

function checkPageLength(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function () {
      const pdfData = new Uint8Array(reader.result);

      pdfjsLib.getDocument(pdfData)
        .promise
        .then(pdfDoc => {
          const numPages = pdfDoc.numPages;

          if (numPages > MaxPages) {
            genericPdfErrorModal(true, 'pdf-error-page-length', MaxPages);
            const message = `Page length is greater than ${MaxPages} pages`;
            console.error(message);
            reject(new Error(message));
          } else {
            resolve();
          }
        })
        .catch(error => {
          console.error('Error in PDF.js:', error);
          reject(error);
        });
    };

    reader.onerror = function (error) {
      console.error('Error reading file:', error);
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
}

function createS3FileName (file) {
  const filename = file.name;

  const folderIdLength = 30;
  let folderId = Array.from({ length: folderIdLength }, () =>
    Math.floor(Math.random() * 36).toString(36)
  ).join('');
  folderId = folderId.substring(0, folderIdLength);

  const name = filename
    .toLowerCase()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .concat('.pdf');

  return encodeURIComponent(`${s3Folder}/${folderId}/${name}`);
}

async function createS3Signature (file) {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  const requestOptions = {
    method: 'GET',
    headers: headers
  };

  const fileName = createS3FileName(file);
  const fileType = encodeURIComponent('application/pdf');

  try {
    const response = await fetch(`https://uploader.webrand.com/s3/params?filename=${fileName}&type=${fileType}`, requestOptions);

    if (!response.ok) {
      throw new Error(`Signature Error. Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    _adapterInstance.trigger('static_pages:pdf-upload-failure');
  }
}

async function uploadFileToS3 (signatureData, file) {
  const formData = new FormData();

  Object.entries(signatureData.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  formData.append('file', file, file.name);

  const requestOptions = {
    method: 'POST',
    body: formData,
    headers: {
      'x-amz-storage-class': 'STANDARD_IA',
    },
  };

  try {
    const result = await fetch(signatureData.url, requestOptions);

    if (!result.ok) {
      throw new Error(`HTTP error! Status: ${result.status}`);
    }
  } catch (error) {
    _adapterInstance.trigger('static_pages:pdf-upload-failure');
    console.error(error);
  }
}

function setDesygnerRedirectionProperties (signatureData, file) {
  try {
    const s3PdfUrl = `https://s3.amazonaws.com/${signatureData.fields.bucket}/${signatureData.fields.key}`;
    const expirationDate = new Date();

    expirationDate.setHours(expirationDate.getHours() + 1);
    const expiresUTC = expirationDate.toUTCString();

    const domain = getDomain();
    document.cookie = `static_pages_pdf_url=${encodeURIComponent(s3PdfUrl)}; expires=${expiresUTC}; path=/;`;
    document.cookie = `static_pages_pdf_filename=${encodeURIComponent(file.name)}; expires=${expiresUTC}; path=/;`;

    //Prevent Desygner onboarding flow when entering Desygner from static pages PDF Viewer flow
    window.localStorage.setItem('ignore-onboarding', true);
    window.localStorage.setItem('is-static-pages-viewer-flow', true);
  } catch (error) {
    _adapterInstance.trigger('static_pages:pdf-upload-failure');
    console.error(error);
  }
}

function getDomain () {
  return window.location.hostname.match(/([^.]+.[^.]+)$/)[0];
}

function prepareCssForViewer () {
  document.documentElement.classList.add('no-scroll');
}

async function initPdfViewerAureliaApp (file = null) {
  return new Promise(async (resolve, reject) => {
    try {
      CONFIG.pages = [{ file: file }];

      if (document.querySelector('[aurelia-app="pdf-viewer/main"]')) {
        document.querySelector('[aurelia-app="pdf-viewer/main"]').remove();
      }

      const appNode = document.createElement('div');
      appNode.id = 'pdf-viewer';
      appNode.setAttribute('aurelia-app', 'pdf-viewer/main');
      appNode.setAttribute('view-mode', 'default');
      appNode.setAttribute('app-flavour', 'static_pages');
      document.body.appendChild(appNode);

      await desygnerFunctions.getAureliaLibraries($(appNode));
      await loadAdapterApp();

      localStorage.setItem('pdf-viewer-showing', 'true');
      prepareCssForViewer();
      resolve();
    } catch (e) {
      console.error(e);
      reject('Could not initialise PDF Viewer');
    }
  });
}

async function loadAdapterApp() {
  const { adapter } = await System.import('@desygner/adapter/adapter');

  await waitForElementToExist(document, '[ref="pdfRenderBoxRef"]');

  _clientAdapter = adapter.getAdapterByDomElement(document.querySelector('[ref="pdfRenderBoxRef"]').parentElement);
  _clientAdapter && _setAdapterInstanceEventListeners();

  _adapterInstance = adapter.registerInstanceByDomElement(this, document.querySelector('[ref="pdfRenderBoxRef"]').parentElement, {
    id: 'pdf-view/pdf-view'
  });
}

function _setAdapterInstanceEventListeners () {
  if (_clientAdapter) {
    _clientAdapter.on('pdf-viewer:redirect-to-desygner', handleEditButtonClicked);
    _clientAdapter.on('pdf-viewer:closed', () => {
      document.documentElement.classList.remove('no-scroll');
      localStorage.removeItem('pdf-viewer-showing');
    });
  }
}

function _toggleSkeleton (show, applyNoscroll = true) {
  document.querySelector('#loading.splash').classList.toggle('out', !show);
  document.querySelector('header.top-menu').classList.toggle('disabled', show);
  applyNoscroll && document.documentElement.classList.toggle('no-scroll', show);
}
